import { useEffect, useState } from "react";

/**
 * "Is the user on a viewport too small for the editor?" hook.
 *
 * We block the flyer builder on small screens because the canvas
 * tools, sidebars and topbar aren't usable below a certain width.
 * The check is viewport-width-based (not UA sniffing) so resizing a
 * desktop browser or rotating a tablet flips the state in real time.
 *
 * Default breakpoint is 900px — phones in any orientation and small
 * tablets in portrait fall below it; iPads in landscape (1024) pass.
 * Callers can override `breakpointPx` if needed.
 *
 * SSR-safe: returns `false` when `window` is unavailable so the
 * builder doesn't briefly flash the blocker during server render.
 *
 * Subscription model
 * ------------------
 * One pair of `resize` / `orientationchange` listeners is shared by
 * every call to this hook. Consumers register their state setter in
 * a module-level Set; the single listener fans out to all of them
 * on each event. Without this, mounting the hook from a list of N
 * components would attach 2*N redundant listeners on `window`.
 * First subscriber attaches the listener; last to unsubscribe
 * removes it.
 */

type SubscriberEntry = [setter: (v: boolean) => void, breakpointPx: number];

const subscribers = new Set<SubscriberEntry>();
let listenersAttached = false;

const getIsMobile = (breakpointPx: number): boolean => {
  if (typeof window === "undefined") return false;
  return window.innerWidth < breakpointPx;
};

const notifyAll = () => {
  subscribers.forEach(([setter, bp]) => {
    setter(getIsMobile(bp));
  });
};

const attachIfNeeded = () => {
  if (listenersAttached || typeof window === "undefined") return;
  window.addEventListener("resize", notifyAll);
  window.addEventListener("orientationchange", notifyAll);
  listenersAttached = true;
};

const detachIfIdle = () => {
  if (!listenersAttached || subscribers.size > 0) return;
  window.removeEventListener("resize", notifyAll);
  window.removeEventListener("orientationchange", notifyAll);
  listenersAttached = false;
};

export const useIsMobileViewport = (breakpointPx = 900): boolean => {
  const [isMobile, setIsMobile] = useState<boolean>(() => getIsMobile(breakpointPx));

  useEffect(() => {
    const entry: SubscriberEntry = [setIsMobile, breakpointPx];
    subscribers.add(entry);
    attachIfNeeded();
    // Sync once after mount in case the viewport changed between
    // the initial render and the effect (StrictMode double-invoke,
    // hot reload, etc.).
    setIsMobile(getIsMobile(breakpointPx));
    return () => {
      subscribers.delete(entry);
      detachIfIdle();
    };
  }, [breakpointPx]);

  return isMobile;
};
